import {
  faXmark as CancelIcon,
  faPen as EditIcon,
  faFloppyDisk as SaveIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import type { AxiosError } from 'axios';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useDraftState from '@/hooks/useDraftState';
import { useMutationWebhookTest } from '@/pages/Administration/Administration.store';
import type { SettingsTypes } from '@/pages/Administration/Administration.types';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';

import type { WebhookConfigurationProps } from './WebhookConfiguration.types';

// Available webhook event types
const WEBHOOK_EVENTS = [
  {
    value: 'create',
    label: 'Create',
    description: 'Triggered when a new ticket is created'
  },
  {
    value: 'update',
    label: 'Update',
    description:
      "Triggered when a ticket or a ticket's tags or comments are updated"
  },
  {
    value: 'close',
    label: 'Close',
    description: 'Triggered when a ticket is closed'
  }
];

const WebhookConfiguration = (props: WebhookConfigurationProps) => {
  const { classes } = useStyles();
  const { orgInfo, handleSave } = props;
  const [editing, isEditing] = useState<boolean>(false);
  const [updatedOrgInfo, updateOrgInfo, resetDraft] = useDraftState(orgInfo);
  const [testEventType, setTestEventType] = useState<string>('create');
  const sendWebhookTestRequest = useMutationWebhookTest(testEventType);
  // Error object the user dismissed by toggling edit mode.
  const [dismissedError, setDismissedError] = useState<unknown>(null);
  const testError = sendWebhookTestRequest.error as AxiosError<{
    detail?: string;
  }> | null;
  const errorMessage =
    sendWebhookTestRequest.isError && testError !== dismissedError
      ? testError?.response?.data?.detail || testError?.message || ''
      : '';

  const handleTest = () => {
    sendWebhookTestRequest.mutate();
  };

  const toggleEditing = () => {
    isEditing(!editing);
    setDismissedError(sendWebhookTestRequest.error);
  };

  const handleWebhookChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateOrgInfo((prev: SettingsTypes) => {
      const newOrgInfo: SettingsTypes = { ...prev };
      const attribute = event.target.name as 'webhook_url' | 'webhook_secret';
      newOrgInfo[attribute] = event.target.value;
      return newOrgInfo;
    });
  };

  const handleWebhookDataTypeChange = (event: SelectChangeEvent) => {
    updateOrgInfo((prev: SettingsTypes) => {
      const newOrgInfo: SettingsTypes = { ...prev };
      newOrgInfo.webhook_data_type = event.target.value;
      return newOrgInfo;
    });
  };

  const handleWebhookEventsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const eventType = event.target.name;
    const isChecked = event.target.checked;

    updateOrgInfo((prev: SettingsTypes) => {
      const newOrgInfo: SettingsTypes = { ...prev };
      const currentEvents = [...(newOrgInfo.webhook_events || ['create'])];

      if (isChecked) {
        // Add event if not already present
        if (!currentEvents.includes(eventType)) {
          currentEvents.push(eventType);
        }
      } else {
        // Remove event but ensure at least one event remains (create by default)
        const filteredEvents = currentEvents.filter((e) => e !== eventType);
        if (filteredEvents.length === 0) {
          // If removing all events, default to 'create'
          newOrgInfo.webhook_events = ['create'];
          return newOrgInfo;
        }
        newOrgInfo.webhook_events = filteredEvents;
        return newOrgInfo;
      }

      newOrgInfo.webhook_events = currentEvents;
      return newOrgInfo;
    });
  };

  const handleTestEventTypeChange = (event: SelectChangeEvent) => {
    setTestEventType(event.target.value);
  };

  return (
    <LargeCard
      title="Webhook Configuration"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                onClick={() => {
                  toggleEditing();
                  handleSave(updatedOrgInfo);
                }}
              >
                <FontAwesomeIcon size="xs" icon={SaveIcon} />
              </IconButton>
              <IconButton
                onClick={() => {
                  toggleEditing();
                  resetDraft();
                }}
              >
                <FontAwesomeIcon size="xs" icon={CancelIcon} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={toggleEditing}>
              <FontAwesomeIcon size="xs" icon={EditIcon} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Typography className={classes.subText} variant="body1">
        We’ll send a POST request to the URL below with details of new tickets.
      </Typography>
      <InputLabel sx={{ mt: '1rem' }} id="webhookURL">
        URL
      </InputLabel>
      <TextField
        className={classes.textField}
        onChange={handleWebhookChange}
        fullWidth
        aria-labelledby="webhook_url"
        name="webhook_url"
        variant="outlined"
        disabled={!editing}
        value={updatedOrgInfo.webhook_url}
      />
      <InputLabel id="webhook_secret">Secret</InputLabel>
      <TextField
        className={classes.textField}
        onChange={handleWebhookChange}
        fullWidth
        aria-labelledby="webhook_secret"
        name="webhook_secret"
        variant="outlined"
        disabled={!editing}
        value={updatedOrgInfo.webhook_secret}
      />
      <InputLabel id="webhook_data_type_label">Webhook Data Type</InputLabel>
      <FormControl className={classes.formControl}>
        <Select
          aria-labelledby="webhook_data_type_label"
          id="webhook_data_type"
          onChange={handleWebhookDataTypeChange}
          disabled={!editing}
          value={updatedOrgInfo.webhook_data_type}
        >
          <MenuItem value="application/json">application/json</MenuItem>
          <MenuItem value="application/xml">application/xml</MenuItem>
        </Select>
      </FormControl>

      {/* Webhook Events Configuration */}
      <FormLabel sx={{ mt: '1rem', mb: '0.5rem', display: 'block' }}>
        Webhook Events
      </FormLabel>
      <Typography
        className={classes.subText}
        variant="body2"
        sx={{ mb: '0.5rem' }}
      >
        Select which events should trigger webhook calls:
      </Typography>
      <FormGroup>
        {WEBHOOK_EVENTS.map((eventType) => {
          const isChecked = (
            updatedOrgInfo.webhook_events || ['create']
          ).includes(eventType.value);
          return (
            <FormControlLabel
              key={eventType.value}
              sx={{ alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleWebhookEventsChange}
                  name={eventType.value}
                  disabled={!editing}
                  sx={{ mt: '-6px' }}
                />
              }
              label={
                <div>
                  <Typography variant="body2" component="span">
                    <strong>{eventType.label}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{ color: 'text.secondary' }}
                  >
                    {eventType.description}
                  </Typography>
                </div>
              }
            />
          );
        })}
      </FormGroup>

      <Divider sx={{ my: 1 }} />

      {/* Test Webhook Section */}
      <Typography variant="h6" sx={{ mt: '1rem' }}>
        Test Webhook
      </Typography>
      <FormControl className={classes.formControl} sx={{ mb: '1rem' }}>
        <Select
          aria-labelledby="test_event_type_label"
          id="test_event_type"
          onChange={handleTestEventTypeChange}
          value={testEventType}
          disabled={!editing}
        >
          {WEBHOOK_EVENTS.map((event) => (
            <MenuItem key={event.value} value={event.value}>
              {event.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        onClick={handleTest}
        sx={{ display: 'block' }}
        variant="outlined"
        disabled={!editing}
      >
        Test Webhook (
        {testEventType.charAt(0).toUpperCase() + testEventType.slice(1)})
      </Button>
      <Typography className={classes.subText} variant="body1" sx={{ mt: 1 }}>
        Please save changes before testing the webhook. The test will send a
        sample ticket to the configured webhook URL.
      </Typography>
      {errorMessage && (
        <Alert severity="error" sx={{ mt: '1rem' }}>
          {errorMessage}
        </Alert>
      )}
    </LargeCard>
  );
};

export default WebhookConfiguration;
